/** DashboardCard — framed card shell for dashboard widgets. Paddings tuned for analytics, not forms. */

import { cn } from '../../shared/ui/utils';

type Tone = 'neutral' | 'brand' | 'warning' | 'danger' | 'success';

const TONE_RAIL: Record<Tone, string> = {
  neutral: '',
  brand: 'before:bg-brand',
  warning: 'before:bg-status-warning',
  danger: 'before:bg-status-danger',
  success: 'before:bg-status-success',
};

type Density = 'comfortable' | 'compact';

interface DashboardCardProps {
  tone?: Tone;
  eyebrow?: string;
  title?: string;
  hint?: string;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  density?: Density;
  /** Borderless variant — no card frame, just a header divider. */
  bare?: boolean;
  /** The screen's focal card: a large heading, no divider. Only with `bare`. */
  lead?: boolean;
}

export function DashboardCard({
  tone = 'neutral',
  eyebrow,
  title,
  hint,
  action,
  footer,
  children,
  className,
  bodyClassName,
  density = 'comfortable',
  bare = false,
  lead = false,
}: DashboardCardProps) {
  const hasToneRail = tone !== 'neutral' && !bare;
  const compact = density === 'compact';

  return (
    <div
      data-slot="dashboard-card"
      className={cn(
        'overflow-hidden relative flex flex-col min-h-0',
        bare
          ? 'bg-transparent border-0'
          : cn(
              // A low Material-style elevation separates analytics from the
              // canvas without making every widget look like a floating panel.
              'bg-card',
              'shadow-[0_1px_2px_rgba(0,0,0,0.045),0_5px_14px_-12px_rgba(0,0,0,0.12)]',
              compact ? 'rounded-lg' : 'rounded-[var(--radius-widget)]',
            ),
        hasToneRail &&
          'before:absolute before:inset-y-3 before:left-0 before:w-[3px] before:rounded-r-full',
        hasToneRail && TONE_RAIL[tone],
        className,
      )}
    >
      {(eyebrow || title || hint != null || action) && (
        <div
          className={cn(
            'flex items-baseline justify-between gap-3 shrink-0',
            bare && lead
              ? 'px-0 pb-1'
              : bare
              ? 'px-0 pt-1 pb-1.5 border-b border-border/40'
              : compact
              ? 'px-3 pt-2.5 pb-2 border-b border-border/40'
              : 'px-5 pt-4 pb-3',
          )}
        >
          <div className="min-w-0">
            {eyebrow && (
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-0.5 font-apple">
                {eyebrow}
              </p>
            )}
            {title && (
              <p
                className={cn(
                  'tracking-tight text-foreground font-apple font-semibold',
                  bare && lead
                    ? 'text-[22px] leading-tight tracking-[-0.015em]'
                    : bare || compact
                    ? 'text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-medium'
                    : 'text-[15px]',
                )}
              >
                {title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hint != null && (
              <span
                className={cn(
                  'text-muted-foreground tabular-nums font-apple',
                  compact ? 'text-[11px]' : 'text-[12px]',
                )}
              >
                {hint}
              </span>
            )}
            {action}
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex-1 min-h-0',
          bare ? 'px-0 py-0' : compact ? 'px-0 pb-0' : 'px-5 pb-5',
          bodyClassName,
        )}
      >
        {children}
      </div>

      {footer && (
        <div
          className={cn(
            'flex items-center justify-between shrink-0',
            bare
              ? 'px-0 py-1.5 border-t border-border/40'
              : cn(
                  'border-t border-border/60',
                  compact ? 'px-3 py-2' : 'px-5 py-2.5',
                ),
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
